// pages/Dashboard/Salary.js
import { useState, useEffect, useCallback } from 'react';
import {
  FaMoneyBillWave,
  FaSearch,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationCircle,
  FaEdit,
  FaTimes,
  FaWallet,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa';
import '../Css/Salary.css';

import { useToast } from '../../components/Toast';
import { baseurl } from '../Baseurl';

const ROLE_STYLES = {
  Stylist: { cls: 'role-stylist' },
  Beautician: { cls: 'role-beautician' },
  'Makeup Artist': { cls: 'role-makeup' },
  'Massage Therapist': { cls: 'role-massage' },
  'Nail Technician': { cls: 'role-nails' },
  Receptionist: { cls: 'role-reception' },
  Manager: { cls: 'role-manager' },
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const PAYMENT_MODES = ['cash', 'bank_transfer', 'upi', 'cheque'];

const EMPTY_FORM = {
  baseSalary: '',
  bonus: '',
  deductions: '',
  advance: '',
  paymentStatus: 'unpaid',
  paymentDate: '',
  paymentMode: '',
  remarks: '',
};

const initials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('');

const fmt = (n) => `₹${Number(n || 0).toLocaleString()}`;

function Salary() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());

  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState({
    totalPayroll: 0,
    totalPaid: 0,
    totalPending: 0,
    paidCount: 0,
    unpaidCount: 0,
    partialCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const [showDrawer, setShowDrawer] = useState(false);
  const [activeStaff, setActiveStaff] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const toast = useToast();

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
  });

  const fetchSalary = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${baseurl}/salary?month=${month}&year=${year}`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to fetch salary records');

      setRecords(data.data.records);
      setStats(data.data.stats);
    } catch (err) {
      setError(err.message);
      toast(err.message || 'Failed to fetch salary records', 'error');
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    fetchSalary();
  }, [fetchSalary]);

  const shiftMonth = (dir) => {
    let m = month + dir;
    let y = year;
    if (m > 12) { m = 1; y += 1; }
    if (m < 1) { m = 12; y -= 1; }
    setMonth(m);
    setYear(y);
  };

const openDrawer = (record) => {
  console.log('Opening drawer for record:', record); // DEBUG

  setActiveStaff(record);
  setForm({
    baseSalary: record.base_salary ?? '',
    bonus: record.bonus ?? '',
    deductions: record.deductions ?? '',
    advance: record.advance ?? '',
    paymentStatus: record.payment_status || 'unpaid',
    paymentDate: record.payment_date ? record.payment_date.slice(0, 10) : '',
    paymentMode: record.payment_mode || '',
    remarks: record.remarks || '',
  });
  setShowDrawer(true);
};


  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const netPreview =
    (Number(form.baseSalary) || 0) +
    (Number(form.bonus) || 0) -
    (Number(form.deductions) || 0) -
    (Number(form.advance) || 0);

  const handleSubmit = async () => {
  if (!form.baseSalary) {
    toast('Base salary is required', 'error');
    return;
  }

  console.log('activeStaff at submit:', activeStaff); // DEBUG

  // Fallback chain in case the API response uses a different key
  const staffId = activeStaff?.staff_id ?? activeStaff?.id ?? activeStaff?.staffId;

  if (!staffId) {
    console.error('No staff id found on activeStaff object:', activeStaff);
    toast('Staff ID is missing, please try again', 'error');
    return;
  }

  setSaving(true);
  setError('');
  try {
    const payload = {
      staffId: Number(staffId),
      month,
      year,
      baseSalary: Number(form.baseSalary),
      bonus: form.bonus ? Number(form.bonus) : 0,
      deductions: form.deductions ? Number(form.deductions) : 0,
      advance: form.advance ? Number(form.advance) : 0,
      paymentStatus: form.paymentStatus,
      paymentDate: form.paymentDate || null,
      paymentMode: form.paymentMode || null,
      remarks: form.remarks,
    };

    console.log('Sending salary payload:', payload); // DEBUG

    const res = await fetch(`${baseurl}/salary`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });

    const data = await res.json();
    console.log('Salary API response:', data); // DEBUG

    if (!res.ok) throw new Error(data.message || 'Failed to save salary record');

    toast(data.message || 'Salary record saved', 'success');
    setShowDrawer(false);
    setActiveStaff(null);
    await fetchSalary();
  } catch (err) {
    setError(err.message);
    toast(err.message || 'Failed to save salary record', 'error');
  } finally {
    setSaving(false);
  }
};

  const filteredRecords = records.filter(
    (r) =>
      r.name.toLowerCase().includes(search.toLowerCase()) ||
      r.employee_code?.toLowerCase().includes(search.toLowerCase())
  );

  const statusBadge = (status) => {
    if (status === 'paid') return { cls: 'status-paid', icon: <FaCheckCircle />, label: 'Paid' };
    if (status === 'partial') return { cls: 'status-partial', icon: <FaExclamationCircle />, label: 'Partial' };
    return { cls: 'status-unpaid', icon: <FaTimesCircle />, label: 'Unpaid' };
  };

  return (
    <div className="salary-page">
      <div className="salary-header">
        <div>
          <h2>Staff Salary</h2>
          <p>Manage monthly salary records and payments</p>
        </div>
        <div className="month-switcher">
          <button className="month-nav-btn" onClick={() => shiftMonth(-1)}>
            <FaChevronLeft />
          </button>
          <span className="month-label">{MONTHS[month - 1]} {year}</span>
          <button className="month-nav-btn" onClick={() => shiftMonth(1)}>
            <FaChevronRight />
          </button>
        </div>
      </div>

      {error && <div className="salary-error-banner">{error}</div>}

      <div className="salary-stats-strip">
        <div className="salary-stat-card">
          <span className="salary-stat-value">{fmt(stats.totalPayroll)}</span>
          <span className="salary-stat-label">Total Payroll</span>
        </div>
        <div className="salary-stat-card">
          <span className="salary-stat-value stat-paid-text">{fmt(stats.totalPaid)}</span>
          <span className="salary-stat-label">Paid ({stats.paidCount})</span>
        </div>
        <div className="salary-stat-card">
          <span className="salary-stat-value stat-pending-text">{fmt(stats.totalPending)}</span>
          <span className="salary-stat-label">Pending ({stats.unpaidCount + stats.partialCount})</span>
        </div>
        <div className="salary-stat-card">
          <span className="salary-stat-value stat-partial-text">{stats.partialCount}</span>
          <span className="salary-stat-label">Partial Payments</span>
        </div>
      </div>

      <div className="salary-toolbar">
        <div className="input-group search-input">
          <FaSearch className="input-icon" />
          <input
            type="text"
            placeholder="Search by name or ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="salary-table-wrapper">
        <table className="salary-table">
          <thead>
            <tr>
              <th>Staff Member</th>
              <th>Role</th>
              <th>Base</th>
              <th>Bonus</th>
              <th>Deductions</th>
              <th>Advance</th>
              <th>Net Salary</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="9" className="salary-empty-row">
                  Loading salary records...
                </td>
              </tr>
            )}

             
{!loading &&
  filteredRecords.map((r) => {
    const badge = statusBadge(r.payment_status || 'unpaid');
    const rowKey = r.staff_id ?? r.employee_code;
    return (
      <tr key={rowKey} className="salary-row">
        <td>
          <div className="table-staff-name">
            <div className="staff-avatar">{initials(r.name)}</div>
            <div>
              <strong>{r.name}</strong>
              <span className="table-sub">{r.employee_code}</span>
            </div>
          </div>
        </td>
        <td>
          <span className={`role-tag ${ROLE_STYLES[r.role]?.cls}`}>{r.role}</span>
        </td>
        <td>{r.base_salary ? fmt(r.base_salary) : '-'}</td>
        <td>{r.bonus ? fmt(r.bonus) : '-'}</td>
        <td>{r.deductions ? fmt(r.deductions) : '-'}</td>
        <td>{r.advance ? fmt(r.advance) : '-'}</td>
        <td><strong>{r.net_salary ? fmt(r.net_salary) : '-'}</strong></td>
        <td>
          <span className={`status-pill ${badge.cls}`}>
            {badge.icon} {r.id ? badge.label : 'Not Set'}
          </span>
        </td>
        <td>
          <button className="table-view-btn" onClick={() => openDrawer(r)}>
            <FaEdit /> {r.id ? 'Edit' : 'Set Salary'}
          </button>
        </td>
      </tr>
    );
  })}

            {!loading && filteredRecords.length === 0 && (
              <tr>
                <td colSpan="9" className="salary-empty-row">
                  No staff members found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showDrawer && activeStaff && (
        <div className="drawer-overlay" onClick={() => setShowDrawer(false)}>
          <div className="drawer-panel" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <div className="drawer-header-info">
                <div className="staff-avatar large">{initials(activeStaff.name)}</div>
                <div>
                  <h3>{activeStaff.name}</h3>
                  <span className="table-sub">{MONTHS[month - 1]} {year} Salary</span>
                </div>
              </div>
              <button className="drawer-close-btn" onClick={() => setShowDrawer(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="drawer-body">
              <div className="drawer-section form-grid">
                <div>
                  <label className="form-label">Base Salary (₹)</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="e.g. 25000"
                    value={form.baseSalary}
                    onChange={(e) => handleFormChange('baseSalary', e.target.value)}
                  />
                </div>
                <div>
                  <label className="form-label">Bonus (₹)</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="e.g. 1000"
                    value={form.bonus}
                    onChange={(e) => handleFormChange('bonus', e.target.value)}
                  />
                </div>
              </div>

              <div className="drawer-section form-grid">
                <div>
                  <label className="form-label">Deductions (₹)</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="e.g. 500"
                    value={form.deductions}
                    onChange={(e) => handleFormChange('deductions', e.target.value)}
                  />
                </div>
                <div>
                  <label className="form-label">Advance Taken (₹)</label>
                  <input
                    type="number"
                    className="form-input"
                    placeholder="e.g. 2000"
                    value={form.advance}
                    onChange={(e) => handleFormChange('advance', e.target.value)}
                  />
                </div>
              </div>

              <div className="drawer-section net-preview-box">
                <FaWallet className="drawer-stat-icon" />
                <div>
                  <span>Net Salary</span>
                  <strong>{fmt(netPreview)}</strong>
                </div>
              </div>

              <div className="drawer-section form-grid">
                <div>
                  <label className="form-label">Payment Status</label>
                  <select
                    className="form-select"
                    value={form.paymentStatus}
                    onChange={(e) => handleFormChange('paymentStatus', e.target.value)}
                  >
                    <option value="unpaid">Unpaid</option>
                    <option value="partial">Partial</option>
                    <option value="paid">Paid</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Payment Mode</label>
                  <select
                    className="form-select"
                    value={form.paymentMode}
                    onChange={(e) => handleFormChange('paymentMode', e.target.value)}
                  >
                    <option value="">Select mode</option>
                    {PAYMENT_MODES.map((m) => (
                      <option key={m} value={m}>{m.replace('_', ' ')}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="drawer-section">
                <label className="form-label">Payment Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={form.paymentDate}
                  onChange={(e) => handleFormChange('paymentDate', e.target.value)}
                />
              </div>

              <div className="drawer-section">
                <label className="form-label">Remarks (optional)</label>
                <textarea
                  className="form-textarea"
                  rows="3"
                  placeholder="Any notes about this payment"
                  value={form.remarks}
                  onChange={(e) => handleFormChange('remarks', e.target.value)}
                ></textarea>
              </div>
            </div>

            <div className="drawer-footer">
              <button className="btn-outline full-width" onClick={() => setShowDrawer(false)}>
                Cancel
              </button>
              <button className="btn-primary full-width" onClick={handleSubmit} disabled={saving}>
                <FaMoneyBillWave /> {saving ? 'Saving...' : 'Save Salary'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Salary;